import type { GrowthRecord } from '@grosync/common';
import { ConfirmGrowthBodySchema, CropConfigBodySchema } from '@grosync/common';
import { z } from 'zod';
import { apiGet, apiGetBlob, apiPatch, apiPost, apiRequest } from './client';

export type ConfirmGrowthBody = z.infer<typeof ConfirmGrowthBodySchema>;
export type CropConfigBody = z.infer<typeof CropConfigBodySchema>;

export interface GrowthListFilter {
  deviceId?: string;
  from?: string;
  to?: string;
  status?: 'pending' | 'confirmed';
  page?: number;
  pageSize?: number;
}

export interface GrowthPage {
  items: GrowthRecord[];
  total: number;
}

export function fetchGrowthRecords(filter: GrowthListFilter): Promise<GrowthPage> {
  const params = new URLSearchParams();
  if (filter.deviceId) params.set('deviceId', filter.deviceId);
  if (filter.from)     params.set('from', filter.from);
  if (filter.to)       params.set('to', filter.to);
  if (filter.status)   params.set('status', filter.status);
  if (filter.page)     params.set('page', String(filter.page));
  if (filter.pageSize) params.set('pageSize', String(filter.pageSize));
  return apiGet<GrowthPage>(`/api/growth?${params}`);
}

export function fetchGrowthRecord(id: string): Promise<GrowthRecord> {
  return apiGet<GrowthRecord>(`/api/growth/${id}`);
}

export function fetchLatestGrowthRecord(deviceId: string): Promise<GrowthRecord | null> {
  return apiGet<GrowthRecord | null>(`/api/growth/device/${deviceId}/latest`);
}

export function confirmGrowthRecord(id: string, body: ConfirmGrowthBody): Promise<GrowthRecord> {
  return apiPatch<GrowthRecord>(`/api/growth/${id}/confirm`, body);
}

export interface ManualUploadResult {
  record: GrowthRecord;
  /** submitting = LRM 에 3D 처리를 맡김 · not_configured = 서버에 LRM 작업 연결이 없음(저장만) */
  lrm: 'submitting' | 'not_configured';
}

/** 관리자 화면에서 생육 사진 올리기 — 저장 · 기록 · (LRM 연결 시) 3D 처리 시작 */
export function uploadGrowthPhoto(
  deviceId: string,
  photo: File,
  recordedAt: string,
): Promise<ManualUploadResult> {
  const form = new FormData();
  form.append('deviceId', deviceId);
  form.append('recordedAt', recordedAt);
  form.append('photo', photo);
  return apiRequest<ManualUploadResult>('/api/growth/photos/manual', { method: 'POST', body: form });
}

export function getPhotoUrl(photoPath: string): string {
  return `/api/growth/uploads/${photoPath.replace('growth/', '')}`;
}

// ── 신규 장비 관부 등록 (LRM 계약 v1.1 프록시) ──────────────────────────────
// 좌표 계약: crown_px·plant_box 는 registration-photo 가 반환한(EXIF 적용) 이미지의 픽셀 좌표.

export interface LrmRegistrationContext {
  device_id: string;
  recorded_at: string;
  registered: boolean;
  measure_config_missing: string | null;
  photo: { url: string; width: number; height: number };
  suggested_plant_box: [number, number, number, number] | null;
  profiles: string[];
}

/** 첫 등록 때 기준 물체 (LRM 계약 2026-10-29) — 그 장비의 측정 설정이 된다. profile 과 함께 보내지 않는다 */
export interface LrmRegistrationMarker {
  side_mm: number;
  type: 'aruco' | 'corners' | 'auto';
  reference_type?: 'wire_cube';
  /** TL·TR·BR·BL, crown_px 와 같은 좌표계 (type: corners) */
  corners_px?: [number, number][];
  shape?: 'square' | 'cube';
  /** 관부 평면이 기준 물체 앞면 **중심**보다 몇 mm 위 (LRM 계약). 없으면 LRM 기본(밑변 = 흙) */
  face_up_mm?: number;
}

/** 기준 물체 자동 검출 (LRM) — 실패면 422 와 사유가 오고, 여기에는 QC 를 통과한 결과만 온다 */
export interface LrmReferenceDetection {
  ok: boolean;
  reference_type: 'wire_cube';
  source: 'auto_detected';
  side_mm: number;
  /** 앞면 바깥 꼭짓점 TL·TR·BR·BL (registration-photo 좌표) */
  corners_px: [number, number][];
  vertices_px?: [number, number][];
  edges?: [number, number][];
  confidence: 'high' | 'low';
  qc: Record<string, unknown>;
  failure_reason?: string | null;
}

export function detectLrmReference(id: string, side_mm: number): Promise<LrmReferenceDetection> {
  return apiPost<LrmReferenceDetection>(`/api/growth/${id}/lrm-reference-detection`, {
    reference_type: 'wire_cube', side_mm,
  });
}

export interface LrmRegistrationBody {
  crown_px: [number, number];
  plant_box?: [number, number, number, number] | null;
  profile?: string | null;
  name?: string | null;
  marker?: LrmRegistrationMarker;
}

export interface LrmRegistrationResult {
  device_id: string;
  registered: true;
  crown_px: [number, number];
  plant_box?: [number, number, number, number] | null;
  cleared_watch_entries: number;
  warnings: string[];
  measure_config_missing: string | null;
}

export function fetchLrmRegistrationContext(id: string, suggest = true): Promise<LrmRegistrationContext> {
  return apiGet<LrmRegistrationContext>(`/api/growth/${id}/lrm-registration-context${suggest ? '' : '?suggest=false'}`);
}

export function fetchLrmRegistrationPhoto(id: string): Promise<Blob> {
  return apiGetBlob(`/api/growth/${id}/lrm-registration-photo`);
}

export function submitLrmRegistration(id: string, body: LrmRegistrationBody): Promise<LrmRegistrationResult> {
  return apiPost<LrmRegistrationResult>(`/api/growth/${id}/lrm-registration`, body);
}

/** LRM 이 관부 등록·측정 설정을 요구하며 멈춘 기록인가 — ai_suggestions.lrm.error.code 로 판정.
 *  제출 때 거절은 not_registered, 처리 중 실패(감시 경로)는 registration_required 로 온다 — 둘 다 관부 등록이다 */
export function lrmRegistrationNeeded(record: GrowthRecord): 'not_registered' | 'measurement_profile_required' | null {
  const lrm = (record.aiSuggestions as { lrm?: { error?: { code?: string } } } | null)?.lrm;
  const code = lrm?.error?.code;
  if (code === 'not_registered' || code === 'registration_required') return 'not_registered';
  return code === 'measurement_profile_required' ? code : null;
}
